import {
  CREATE_SUBENQUIRY_STATUS,
  GET_MAIN_STATUSES,
  GET_SUB_STATUSES,
  UPDATE_SUBENQUIRY_STATUS,
  get_headers,
  get_project_id,
  get_token,
  DELETE_SUB_STATUS
} from '@/utils/constants/constants.js';
import alert_mixin from '@/utils/mixins/alert_mixin';
import sanitize from '@/utils/sanitize';
import axios from 'axios';
import CreateTransitionRules from '../create_transition_rules/CreateTransitionRules.vue';
import NavigationBackButton from "@/components/probaworkflows_base_components/NavigationBackButton/NavigationBackButton.vue";

// Helpers to safely extract properties
function safe_get(obj, key, default_val = '') {
  const desc = Object.getOwnPropertyDescriptor(obj || {}, key);
  return desc && desc.enumerable ? desc.value : default_val;
}

function safe_get_boolean(obj, key) {
  const val = safe_get(obj, key);
  return typeof val === 'boolean' ? val : false;
}

function isValidStageObject(obj) {
  return obj && typeof obj === 'object' && !Array.isArray(obj);
}

function isPlainObject(obj) {
  return typeof obj === 'object' && obj !== null && !Array.isArray(obj);
}

function getSafeSubStage(sub_stages, index) {
  const isValidIndex = Number.isInteger(index) && index >= 0;
  const isValidArray = Array.isArray(sub_stages) && isValidIndex && index < sub_stages.length;
  const candidate = isValidArray ? sub_stages.at(index) : null;

  const isValidCandidate = isPlainObject(candidate);

  if (!isValidCandidate) return {};

  return Object.create(null, Object.getOwnPropertyDescriptors(candidate));
}

function validateSubStage(obj) {
  return (
    isPlainObject(obj) &&
    typeof safe_get(obj, 'id') === 'number' &&
    typeof safe_get(obj, 'name') === 'string' &&
    typeof safe_get(obj, 'description') === 'string' &&
    typeof safe_get_boolean(obj, 'is_mandatory') === 'boolean' &&
    typeof safe_get_boolean(obj, 'editing') === 'boolean'
  );
}



export default {
  name: 'CreateEnquiryStages',
  components: { CreateTransitionRules, NavigationBackButton },
  mixins: [alert_mixin],
  data: () => ({
    headers: get_headers(),
    token: get_token(),
    project_id: get_project_id(),
    main_status_id: null,
    enquiry_sub_status: '',
    sub_status_id: null,
    main_stages: [],
    new_sub_stage: {
      name: '', 
      description: '', 
      is_mandatory: false,
      next_sub_status_id: null,   // Add these
      next_main_status_id: null   // Add these
    },
    current_stage_index: null,
    add_sub_stage_modal: null,
    loading: false,
    active_tab: 'Manage Status'
  }),
  computed: {
    current_main_stage_name() {
      if (Number.isSafeInteger(this.current_stage_index)) {
        const raw_name = safe_get(this.main_stages[this.current_stage_index], 'name');
        return sanitize(String(raw_name).substring(0, 255));
      }
      return '';
    },
    current_main_stage_id() {
      if (Number.isSafeInteger(this.current_stage_index)) {
        const raw_id = safe_get(this.main_stages[this.current_stage_index], 'id');
        const id_num = Number(raw_id);
        return Number.isSafeInteger(id_num) ? id_num : null;
      }
      return null;
    }
  },
  mounted() {
    this.get_enquiry_main_statuses();
  },
  methods: {
    set_active_tab(tab) {
      this.active_tab = tab;
    },

    get_sub_status_display_value(sub_stage, all_sub_stages) {
      let display_value = sub_stage.name;
      if (sub_stage.next_sub_status_id) {
        const next_sub = all_sub_stages.find(s => s.id === sub_stage.next_sub_status_id);
        if (next_sub) {
          display_value += `  ->  Next Sub-status : ${next_sub.name}`;
        }
      } else if (sub_stage.next_main_status_id) {
        const next_main = this.main_stages.find(s => s.id === sub_stage.next_main_status_id);
        if (next_main) {
          display_value += ` ->  Next Main Status: ${next_main.name}`;
        }
      }
      return display_value;
    },

    get_current_stage_sub_statuses() {
      if (
        !Number.isSafeInteger(this.current_stage_index) ||
        typeof this.main_stages[this.current_stage_index] !== 'object' ||
        this.main_stages[this.current_stage_index] === null
      ) {
        return [];
      }
      const subs = this.main_stages[this.current_stage_index].sub_stages;
      return Array.isArray(subs) ? subs : [];
    },

    get_available_sub_statuses(main_status_id, exclude_id = null) {
      const main_stage = this.main_stages.find(s => s.id === main_status_id);
      if (!main_stage || !Array.isArray(main_stage.sub_stages)) return [];
      return main_stage.sub_stages.filter(sub => sub.id !== exclude_id);
    },

    validate_next_status(main_status_id, sub_status_id, next_main_id, next_sub_id) {
      // Check if next status points back to current status
      return !(next_main_id === main_status_id && next_sub_id === sub_status_id);
    },
 
    async get_enquiry_main_statuses() {
      this.loading = true;
      try {
        const response = await axios.get(GET_MAIN_STATUSES, { headers: this.headers });
        const list = Array.isArray(response.data.data) ? response.data.data : [];
        this.main_stages = list.map(status => {
          const raw_id = safe_get(status, 'id');
          const id_num = Number(raw_id);
          const raw_name = safe_get(status, 'status');
          return {
            id: Number.isInteger(id_num) ? id_num : 0,
            name: sanitize(String(raw_name).substring(0, 255)),
            expanded: false,
            sub_stages: []
          };
        });
      } catch (error) {
        const msg = sanitize(String(safe_get(error.response?.data, 'message', 'Unknown error')).substring(0, 255));
        this.show_error(`Error fetching main statuses: ${msg}`);
      } finally {
        this.loading = false;
      }
    },
 
    open_add_sub_stage_modal(index) {
      if (!Number.isSafeInteger(index) || index < 0 || index >= this.main_stages.length) {
        this.current_stage_index = null;
        this.main_status_id = null;
        this.enquiry_sub_status = '';
        return;
      }
      this.current_stage_index = index;
      const stage = this.main_stages[index] || {};
      const raw_id = safe_get(stage, 'id');
      this.main_status_id = Number.isSafeInteger(Number(raw_id)) ? String(raw_id) : null;
      this.enquiry_sub_status = '';
    },
 
    async create_enquiry_sub_status() {
      if (!String(this.new_sub_stage.name).trim()) {
        this.show_error('Sub-status name is required');
        return;
      }
      // Add validation
      if (!this.validate_next_status(
        this.current_main_stage_id,
        null,
        this.new_sub_stage.next_main_status_id,
        this.new_sub_stage.next_sub_status_id
      )) {
        this.show_error('Cannot set next status to point back to itself');
        return;
      }
      this.loading = true;
      try {
        const payload = {
          main_status_id: this.current_main_stage_id,
          enquiry_sub_status: sanitize(String(this.new_sub_stage.name).substring(0, 255)),
          next_sub_enquiry_status_id: this.new_sub_stage.next_sub_enquiry_status_id || null,
          next_enquiry_main_status_id: this.new_sub_stage.next_main_status_id || null,
          description: sanitize(String(this.new_sub_stage.description).substring(0, 255)),
          is_mandatory: Boolean(this.new_sub_stage.is_mandatory)
        };
        const response = await axios.post(CREATE_SUBENQUIRY_STATUS, payload, { headers: this.headers });
        const data = safe_get(response.data, 'data', {});
        const id_num = Number(safe_get(data, 'id'));
        const new_sub_status = {
          id: Number.isInteger(id_num) ? id_num : 0,
          name: sanitize(String(this.new_sub_stage.name).substring(0, 255)),
          description: sanitize(String(this.new_sub_stage.description).substring(0, 255)),
          next_sub_enquiry_status_id: this.new_sub_stage.next_sub_status_id || null,
          next_enquiry_main_status_id: this.new_sub_stage.next_main_status_id || null,
          is_mandatory: Boolean(this.new_sub_stage.is_mandatory),
          editing: false
        };
        const main_stage = this.main_stages.find(s => s.id === this.current_main_stage_id);
        if (main_stage && Array.isArray(main_stage.sub_stages)) main_stage.sub_stages.push(new_sub_status);
        this.new_sub_stage = { 
          name: '', 
          description: '', 
          is_mandatory: false,
          next_sub_status_id: null,
          next_main_status_id: null
        };
        this.show_success('Sub-status created successfully!');
        this.close_modal('add_sub_stage');
      } catch (error) {
        const msg = sanitize(String(safe_get(error.response?.data, 'message', 'Failed to create sub-status')).substring(0, 255));
        this.show_error(msg);
      } finally {
        this.loading = false;
      }
    },
 
    async get_enquiry_sub_statuses(main_status_id) {
      if (!Number.isSafeInteger(main_status_id)) return;
      this.loading = true;
      try {
        const params = { main_status_id: Number(main_status_id) };
        const response = await axios.get(GET_SUB_STATUSES, { headers: this.headers, params });
        const list = Array.isArray(response.data.data) ? response.data.data : [];
        const main_stage = this.main_stages.find(s => s.id === Number(main_status_id));
        if (main_stage) {
          main_stage.sub_stages = list.map(sub => {
            const id_num = Number(safe_get(sub, 'id'));
            const raw_name = safe_get(sub, 'enquiry_sub_status');
            const raw_desc = safe_get(sub, 'description');
            return {
              id: Number.isInteger(id_num) ? id_num : 0,
              name: sanitize(String(raw_name).substring(0, 255)),
              description: sanitize(String(raw_desc).substring(0, 255)),
              is_mandatory: safe_get_boolean(sub, 'is_mandatory'),
              next_sub_status_id: Number(safe_get(sub, 'next_sub_enquiry_status')) || null, // Updated this line
              next_main_status_id: Number(safe_get(sub, 'next_enquiry_main_status')) || null, // Updated this line
              editing: false
            };
          });
        }
      } catch (error) {
        const msg = sanitize(String(safe_get(error.response?.data, 'message', 'Unknown error')).substring(0, 255));
        this.show_error(`Error fetching sub statuses: ${msg}`);
      } finally {
        this.loading = false;
      }
    },

   toggle_edit_stage(index) {
      if (
        !Number.isInteger(index) ||
        index < 0 ||
        index >= this.main_stages.length
      ) return;

      const stage_candidate = this.main_stages.at(index);
      const stage = (
        stage_candidate &&
        typeof stage_candidate === 'object' &&
        !Array.isArray(stage_candidate) &&
        isPlainObject(stage_candidate)
      ) ? { ...stage_candidate } : {};

      stage.editing = !safe_get(stage, 'editing', false);

      this.main_stages.splice(index, 1, stage);
    },

    async remove_sub_stage(stage_index, sub_stage_index) {
      if (
        !Number.isInteger(stage_index) ||
        !Number.isInteger(sub_stage_index) ||
        stage_index < 0 ||
        stage_index >= this.main_stages.length
      ) return;

      const stage = this.main_stages.at(stage_index);
      if (!stage || !Array.isArray(stage.sub_stages)) return;

      const sub_stage = stage.sub_stages.at(sub_stage_index);
      if (!sub_stage || !sub_stage.id) return;

      // Use your SweetAlert confirm
      this.show_confirm(
        "Delete Sub-status?",
        `Are you sure you want to delete "${sub_stage.name}"?`,
        async () => {
          this.loading = true;
          try {
            const payload = { sub_status_id: sub_stage.id };
            const response = await axios.post(DELETE_SUB_STATUS, payload, { headers: this.headers });

            if (response.data.status === "success") {
              // remove sub-stage from local state
              const updated_sub_stages = stage.sub_stages.filter(s => s.id !== sub_stage.id);
              const updated_stage = { ...stage, sub_stages: updated_sub_stages };
              this.main_stages.splice(stage_index, 1, updated_stage);

              this.show_success(response.data.message || "Deleted successfully");
            } else {
              this.show_error(response.data.message || "Failed to delete sub-status");
            }
          } catch (error) {
            const msg = String(error.response?.data?.message || "Unknown error").substring(0, 255);
            this.show_error(`Error deleting sub-status: ${msg}`);
          } finally {
            this.loading = false;
          }
        }
      );
    },


    toggle_edit_sub_stage(stage_index, sub_stage_index) {
      if (
        !Number.isInteger(stage_index) ||
        !Number.isInteger(sub_stage_index) ||
        stage_index < 0 ||
        stage_index >= this.main_stages.length
      ) return;

      const source_stage = this.main_stages.at(stage_index);
      if (!isValidStageObject(source_stage)) return;

      // Deep clone only after validation
      const original_stage = structuredClone(source_stage);

      const stage = isPlainObject(original_stage) ? original_stage : {};
      const sub_stages_array = safe_get(stage, 'sub_stages', []);

      if (
        !isValidStageObject(stage) ||
        !Array.isArray(sub_stages_array) ||
        sub_stage_index < 0 ||
        sub_stage_index >= sub_stages_array.length
      ) return;

      const unsafe_sub_stage = getSafeSubStage(sub_stages_array, sub_stage_index);
      const sub_stage_data = isPlainObject(unsafe_sub_stage) ? unsafe_sub_stage : {};
      if (!isValidStageObject(sub_stage_data)) return;
      const raw_sub_stage = {
        id:safe_get(sub_stage_data, 'id') || null,
        name: sanitize(String(safe_get(sub_stage_data, 'name')).substring(0, 255)),
        description: sanitize(String(safe_get(sub_stage_data, 'description')).substring(0, 255)),
        is_mandatory: safe_get_boolean(sub_stage_data, 'is_mandatory'),
        editing: safe_get_boolean(sub_stage_data, 'editing'),
        next_sub_status_id: safe_get(sub_stage_data, 'next_sub_status_id') || null,
        next_main_status_id: safe_get(sub_stage_data, 'next_main_status_id') || null
      };

      if (raw_sub_stage.editing) {
        return this.edit_enquiry_sub_status(stage_index, sub_stage_index);
      }

      const updated_stages = [...this.main_stages];
      updated_stages[stage_index].sub_stages[sub_stage_index] = {
        ...raw_sub_stage,
        editing: true,
        original_name: raw_sub_stage.name,
        original_description: raw_sub_stage.description,
        original_is_mandatory: raw_sub_stage.is_mandatory
      };

      this.main_stages = updated_stages;
    },

    toggle_stage(index) {
      if (
        !Number.isInteger(index) ||
        index < 0 ||
        index >= this.main_stages?.length
      ) return;

      const updated_stages = [...this.main_stages];

      const stage = Reflect.get(updated_stages, index);
      if (!stage || typeof stage !== 'object') return;

      const current_expanded = Reflect.get(stage, 'expanded');
      Reflect.set(stage, 'expanded', !current_expanded);

      const is_expanded = Reflect.get(stage, 'expanded');

      const stage_sub_stages = Reflect.get(stage, 'sub_stages');
      const stage_id = Reflect.get(stage, 'id');
      const has_no_sub_stages = !Array.isArray(stage_sub_stages) || stage_sub_stages.length === 0;

      if (is_expanded && has_no_sub_stages && stage_id) {
        this.get_enquiry_sub_statuses(stage_id);
      }

      Reflect.set(this, 'main_stages', updated_stages);
    },

    async edit_enquiry_sub_status(stage_index, sub_stage_index) {

      if (
        !Number.isInteger(stage_index) ||
        stage_index < 0 ||
        stage_index >= this.main_stages.length
      ) {
        return;
      }

      // Shallow clone of main_stages array
      const updated = this.main_stages.slice();

      // Access stage safely without []
      const original_stage = updated.at(stage_index);
      if (
        !original_stage ||
        typeof original_stage !== 'object' ||
        !Array.isArray(original_stage.sub_stages)
      ) {
        return;
      }

      // Explicit shallow clone with whitelisting
      const stage_obj = {
        id: Number(safe_get(original_stage, 'id')),
        name: sanitize(String(safe_get(original_stage, 'name')).substring(0, 255)),
        sub_stages: original_stage.sub_stages.slice() // clone sub_stages array
      };
      // Validate sub_stage_index
      if (
        !Number.isInteger(sub_stage_index) ||
        sub_stage_index < 0 ||
        sub_stage_index >= stage_obj.sub_stages.length
      ) {
        return;
      }

      // Access sub_stage safely without []
      const candidate = stage_obj.sub_stages.at(sub_stage_index);
      if (
        !candidate ||
        typeof candidate !== 'object' ||
        Array.isArray(candidate) ||
        !isPlainObject(candidate)
      ) {
        return;
      }

      // Whitelist fields explicitly
      const sub_source = {
        id: candidate.id,
        name: candidate.name,
        description: candidate.description,
        is_mandatory: candidate.is_mandatory,
        editing: candidate.editing,
        next_sub_status_id: candidate.next_sub_status_id,
        next_main_status_id: candidate.next_main_status_id
      };

      // Validate and sanitize sub_source
      if (!validateSubStage(sub_source)) {
        return;
      }

      const candidate_sub = {
        id: Number(safe_get(sub_source, 'id')),
        name: sanitize(String(safe_get(sub_source, 'name')).substring(0, 255)),
        description: sanitize(String(safe_get(sub_source, 'description')).substring(0, 255)),
        is_mandatory: safe_get_boolean(sub_source, 'is_mandatory'),
        editing: safe_get_boolean(sub_source, 'editing'),
        next_sub_status_id: safe_get(sub_source, 'next_sub_status_id') || null,
        next_main_status_id: safe_get(sub_source, 'next_main_status_id') || null
      };

      if (!candidate_sub.name.trim()) {
        this.show_error('Sub-status name is required');
        return;
      }

      this.loading = true;
      try {
        const payload = {
          sub_status_id: Number.isInteger(Number(safe_get(candidate_sub, 'id')))
        ? Number(safe_get(candidate_sub, 'id'))
        : 0,
          enquiry_sub_status: candidate_sub.name,
          description: candidate_sub.description,
          is_mandatory: candidate_sub.is_mandatory,
          next_sub_enquiry_status_id: candidate_sub.next_sub_status_id,
          next_enquiry_main_status_id: candidate_sub.next_main_status_id
        };

        const response = await axios.post(UPDATE_SUBENQUIRY_STATUS, payload, {
          headers: this.headers
        });

        if (safe_get(response.data, 'status') === 'success') {
          // Clone sub_stages without []
          const sub_stages_copy = stage_obj.sub_stages.slice();

          // Replace sub_stage without using splice with []
          const first_part = sub_stages_copy.slice(0, sub_stage_index);
          const last_part = sub_stages_copy.slice(sub_stage_index + 1);

          const new_sub_stage = {
        id: payload.sub_status_id,
        name: payload.enquiry_sub_status,
        description: payload.description,
        is_mandatory: payload.is_mandatory,
        next_sub_status_id: payload.next_sub_enquiry_status_id,
        next_main_status_id: payload.next_enquiry_main_status_id,
        editing: false
          };

          const new_sub_stages = first_part.concat([new_sub_stage], last_part);

          // Clone updated array without []
          const updated_first = updated.slice(0, stage_index);
          const updated_last = updated.slice(stage_index + 1);

          const updated_stage = {
        id: stage_obj.id,
        name: stage_obj.name,
        sub_stages: new_sub_stages
          };

          const new_updated = updated_first.concat([updated_stage], updated_last);

          this.main_stages = new_updated;

          this.show_success('Sub-status updated successfully');
        }
      } catch (error) {
        const msg = sanitize(
          String(safe_get(error.response?.data, 'message', 'Unknown error')).substring(0, 255)
        );
        this.show_error(`Error updating sub-status: ${msg}`);
      } finally {
        this.loading = false;
      }
    }

  }
};